/**
 * Layout Registry — predefined grid templates keyed by chart count + case ID.
 *
 * Each LayoutCase describes:
 *   - A CSS Grid template (rows × columns as fr/% strings)
 *   - Per-tile placement within that grid
 *   - Per-tile "weight" (fraction of wrapper area) used by ChartScaleProvider
 *
 * Adding new cases is a data-only change — no component code needs to touch.
 *
 * Grid line numbering reminder:
 *   A 3-column grid has lines 1–4.  "col 2 / 4" spans columns 2 and 3.
 *   A 2-row grid has lines 1–3.     "row 1 / 3" spans both rows.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TilePlacement {
	gridRow: string;
	gridColumn: string;
	widthFraction: number;
	heightFraction: number;
}

export interface LayoutCase {
	gridTemplateColumns: string;
	gridTemplateRows: string;
	rowCount: number;
	colCount: number;
	tiles: TilePlacement[];
}

// ─────────────────────────────────────────────────────────────────────────────
// 2-chart layouts  (screenshot cases 1–4)
// ─────────────────────────────────────────────────────────────────────────────

const TWO_CHART_CASES: Record<string, LayoutCase> = {
	// Case 1: [1][2] equal side-by-side
	case1: {
		gridTemplateColumns: "1fr 1fr",
		gridTemplateRows: "1fr",
		rowCount: 1,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 1.0 },
		],
	},
	// Case 2: [1] top / [2] bottom — stacked
	case2: {
		gridTemplateColumns: "1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 1,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1.0, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1.0, heightFraction: 0.5 },
		],
	},
	// Case 3: [1 70%][2 30%] — left larger
	case3: {
		gridTemplateColumns: "7fr 3fr",
		gridTemplateRows: "1fr",
		rowCount: 1,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.7, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.3, heightFraction: 1.0 },
		],
	},
	// Case 4: [1 30%][2 70%] — right larger
	case4: {
		gridTemplateColumns: "3fr 7fr",
		gridTemplateRows: "1fr",
		rowCount: 1,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.7, heightFraction: 1.0 },
		],
	},
};

// ─────────────────────────────────────────────────────────────────────────────
// 3-chart layouts  (screenshot cases 5–11)
// ─────────────────────────────────────────────────────────────────────────────

const THREE_CHART_CASES: Record<string, LayoutCase> = {
	// Case 5: [1][2][3] equal row
	case1: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr",
		rowCount: 1,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 1.0 },
		],
	},
	// Case 6: [1] top full / [2][3] bottom equal
	case2: {
		gridTemplateColumns: "1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 1.0, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 0.5 },
		],
	},
	// Case 7: [1] top-left [2] bottom-left / [3] right tall — 50/50 cols
	case3: {
		gridTemplateColumns: "1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "1 / 3", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 1.0 },
		],
	},
	// Case 8: [1] left tall NARROW / [2] top-right [3] bottom-right — 30/70
	case4: {
		gridTemplateColumns: "3fr 7fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 3", gridColumn: "1 / 2", widthFraction: 0.3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.7, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 0.7, heightFraction: 0.5 },
		],
	},
	// Case 9: [1] left tall WIDE / [2] top-right [3] bottom-right — 70/30
	case5: {
		gridTemplateColumns: "7fr 3fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 3", gridColumn: "1 / 2", widthFraction: 0.7, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 0.3, heightFraction: 0.5 },
		],
	},
	// Case 10: [1] top-left [2] bottom-left NARROW / [3] right tall WIDE — 30/70
	case6: {
		gridTemplateColumns: "3fr 7fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 0.3, heightFraction: 0.5 },
			{ gridRow: "1 / 3", gridColumn: "2 / 3", widthFraction: 0.7, heightFraction: 1.0 },
		],
	},
	// Case 11: [1][2] top equal / [3] bottom full
	case7: {
		gridTemplateColumns: "1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 3", widthFraction: 1.0, heightFraction: 0.5 },
		],
	},
};

// ─────────────────────────────────────────────────────────────────────────────
// 4-chart layouts  (screenshot cases 12–23)
// ─────────────────────────────────────────────────────────────────────────────

const FOUR_CHART_CASES: Record<string, LayoutCase> = {
	// Case 12: [1][2] / [3][4] — 2×2 equal
	case1: {
		gridTemplateColumns: "1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 0.5 },
		],
	},
	// Case 13: [1] top full / [2][3][4] bottom 3 equal
	case2: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 4", widthFraction: 1.0, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 14: [1][2][3] top 3 equal / [4] bottom full
	case3: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 4", widthFraction: 1.0, heightFraction: 0.5 },
		],
	},
	// Case 15: [1][2] tall (full height) + [3][4] stacked right
	//  ┌───┬───┬───┐
	//  │   │   │ 3 │
	//  │ 1 │ 2 ├───┤
	//  │   │   │ 4 │
	//  └───┴───┴───┘
	case4: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 16: zigzag — [1] 1/3 + [2] 2/3 top, [3] 2/3 + [4] 1/3 bottom
	//  ┌────┬─────────┐
	//  │ 1  │    2    │
	//  ├─────────┬────┤
	//  │    3    │ 4  │
	//  └─────────┴────┘
	case5: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 4", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 3", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 17: reverse zigzag — [1] 2/3 + [2] 1/3 top, [3] 1/3 + [4] 2/3 bottom
	//  ┌─────────┬────┐
	//  │    1    │ 2  │
	//  ├────┬─────────┤
	//  │ 3  │    4    │
	//  └────┴─────────┘
	case6: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 4", widthFraction: 2 / 3, heightFraction: 0.5 },
		],
	},
	// Case 18: column-biased left — [1] 2/3 + [3] 1/3 top, [2] 2/3 + [4] 1/3 bottom
	//  ┌─────────┬────┐
	//  │    1    │ 3  │
	//  ├─────────┼────┤
	//  │    2    │ 4  │
	//  └─────────┴────┘
	case7: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 3", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 19: column-biased right — [1] 1/3 + [2] 2/3 top, [3] 1/3 + [4] 2/3 bottom
	//  ┌────┬─────────┐
	//  │ 1  │    2    │
	//  ├────┼─────────┤
	//  │ 3  │    4    │
	//  └────┴─────────┘
	case8: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 4", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 4", widthFraction: 2 / 3, heightFraction: 0.5 },
		],
	},
	// Case 20: [1][2] top-left pair + [3] right tall + [4] bottom-left wide
	//  ┌───┬───┬───┐
	//  │ 1 │ 2 │   │
	//  ├───┴───┤ 3 │
	//  │   4   │   │
	//  └───────┴───┘
	case9: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "2 / 3", gridColumn: "1 / 3", widthFraction: 2 / 3, heightFraction: 0.5 },
		],
	},
	// Case 21: [1] top-left wide + [2] right tall + [3][4] bottom-left pair
	//  ┌───────┬───┐
	//  │   1   │   │
	//  ├───┬───┤ 2 │
	//  │ 3 │ 4 │   │
	//  └───┴───┴───┘
	case10: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 22: [1] left tall + [2] top-right wide + [3][4] bottom-right pair
	//  ┌───┬───────┐
	//  │   │   2   │
	//  │ 1 ├───┬───┤
	//  │   │ 3 │ 4 │
	//  └───┴───┴───┘
	case11: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 4", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 23: [1] left tall + [2][3] top-right pair + [4] bottom-right wide
	//  ┌───┬───┬───┐
	//  │   │ 2 │ 3 │
	//  │ 1 ├───┴───┤
	//  │   │   4   │
	//  └───┴───────┘
	case12: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 4", widthFraction: 2 / 3, heightFraction: 0.5 },
		],
	},
};

// ─────────────────────────────────────────────────────────────────────────────
// 5-chart layouts  (screenshot cases 26–32)
//
// Layouts that mix 2-col and 3-col rows use a 6-column grid (LCM of 2 & 3)
// so both row types align cleanly.
// ─────────────────────────────────────────────────────────────────────────────

const FIVE_CHART_CASES: Record<string, LayoutCase> = {
	// Case 26: [1] 2/3 + [2] 1/3 top, [3][4][5] equal bottom
	//  ┌────────────┬──────┐
	//  │     1      │  2   │
	//  ├──────┬─────┴┬─────┤
	//  │  3   │  4   │  5  │
	//  └──────┴──────┴─────┘
	case1: {
		gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 6,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 5", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "5 / 7", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 5", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "5 / 7", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 27: [1][2][3] equal top, [4][5] equal bottom (full-width)
	//  ┌──────┬──────┬─────┐
	//  │  1   │  2   │  3  │
	//  ├─────────┬─────────┤
	//  │    4    │    5    │
	//  └─────────┴─────────┘
	case2: {
		gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 6,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 5", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "5 / 7", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 4", widthFraction: 0.5, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "4 / 7", widthFraction: 0.5, heightFraction: 0.5 },
		],
	},
	// Case 28: [1] 1/3 + [2] 2/3 top, [3][4][5] equal bottom
	//  ┌──────┬────────────┐
	//  │  1   │     2      │
	//  ├──────┬──────┬─────┤
	//  │  3   │  4   │  5  │
	//  └──────┴──────┴─────┘
	case3: {
		gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 6,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 7", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 5", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "5 / 7", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 29: [1][2][3] equal top, [4] 2/3 + [5] 1/3 bottom
	//  ┌──────┬──────┬─────┐
	//  │  1   │  2   │  3  │
	//  ├────────────┬──────┤
	//  │     4      │  5   │
	//  └────────────┴──────┘
	case4: {
		gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 6,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 5", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "5 / 7", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 5", widthFraction: 2 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "5 / 7", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 30: [1] left tall + [2][3] top-right + [4][5] bottom-right
	//  ┌───┬───┬───┐
	//  │   │ 2 │ 3 │
	//  │ 1 ├───┼───┤
	//  │   │ 4 │ 5 │
	//  └───┴───┴───┘
	case5: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 31: [1] top-left [2] bottom-left + [3] middle tall + [4] top-right [5] bottom-right
	//  ┌───┬───┬───┐
	//  │ 1 │   │ 4 │
	//  ├───┤ 3 ├───┤
	//  │ 2 │   │ 5 │
	//  └───┴───┴───┘
	case6: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 1.0 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// Case 32 (5-chart): [1][2] top-left + [5] right tall + [3][4] bottom-left
	//  ┌───┬───┬───┐
	//  │ 1 │ 2 │   │
	//  ├───┼───┤ 5 │
	//  │ 3 │ 4 │   │
	//  └───┴───┴───┘
	case7: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 1.0 },
		],
	},
};

// ─────────────────────────────────────────────────────────────────────────────
// 6-chart layouts  (screenshot case 32 = 3×2 grid)
// ─────────────────────────────────────────────────────────────────────────────

const SIX_CHART_CASES: Record<string, LayoutCase> = {
	// Case 32 (6-chart): [1][2][3] / [4][5][6] — 3×2 equal grid
	case1: {
		gridTemplateColumns: "1fr 1fr 1fr",
		gridTemplateRows: "1fr 1fr",
		rowCount: 2,
		colCount: 3,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "1 / 2", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 1 / 3, heightFraction: 0.5 },
			{ gridRow: "2 / 3", gridColumn: "3 / 4", widthFraction: 1 / 3, heightFraction: 0.5 },
		],
	},
	// 2×3 grid (2 cols, 3 rows)
	case2: {
		gridTemplateColumns: "1fr 1fr",
		gridTemplateRows: "1fr 1fr 1fr",
		rowCount: 3,
		colCount: 2,
		tiles: [
			{ gridRow: "1 / 2", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 1 / 3 },
			{ gridRow: "1 / 2", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 1 / 3 },
			{ gridRow: "2 / 3", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 1 / 3 },
			{ gridRow: "2 / 3", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 1 / 3 },
			{ gridRow: "3 / 4", gridColumn: "1 / 2", widthFraction: 0.5, heightFraction: 1 / 3 },
			{ gridRow: "3 / 4", gridColumn: "2 / 3", widthFraction: 0.5, heightFraction: 1 / 3 },
		],
	},
};

// ─────────────────────────────────────────────────────────────────────────────
// Master registry
// ─────────────────────────────────────────────────────────────────────────────

const LAYOUT_REGISTRY: Record<number, Record<string, LayoutCase>> = {
	2: TWO_CHART_CASES,
	3: THREE_CHART_CASES,
	4: FOUR_CHART_CASES,
	5: FIVE_CHART_CASES,
	6: SIX_CHART_CASES,
};

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export function getLayoutCase(chartCount: number, caseId: string): LayoutCase | null {
	return LAYOUT_REGISTRY[chartCount]?.[caseId] ?? null;
}

export function getDefaultLayoutCase(chartCount: number): LayoutCase | null {
	return getLayoutCase(chartCount, "case1");
}

export function getAvailableCases(chartCount: number): string[] {
	const bucket = LAYOUT_REGISTRY[chartCount];
	return bucket ? Object.keys(bucket) : [];
}

export function isValidLayoutCase(chartCount: number, caseId: string): boolean {
	return !!LAYOUT_REGISTRY[chartCount]?.[caseId];
}
